import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  editSubscriptionPlan,
  ERROR_MESSAGES,
} from '@/epics/subscriptionManagment/api/editSubscriptionPlanApiService';
import {
  viewSubscriptionPlan,
  type SubscriptionPlan,
} from '@/epics/subscriptionManagment/api/viewSubscriptionPlanApiService';
import { updateSubscriptionStatusApiService } from '@/epics/subscriptionManagment/api/updateSubscriptionStatusApiService';
import { SUBSCRIPTION_ERROR_MESSAGES } from '@/epics/subscriptionManagment/errors';
import {
  getCycleOptions,
  formatPriceWithSpaces,
  clampPriceToMax,
  getBillingDescription,
  type CreateSubscriptionFormData,
  type FormErrors,
} from './useCreateSubscription';
import {
  BILLING_CYCLE_LABELS,
  formatDuration,
} from './useViewSubscriptionPlanContent';

// ============================================================================
// Types
// ============================================================================

export type EditSubscriptionFormProps = {
  formData: CreateSubscriptionFormData;
  setFormData: React.Dispatch<React.SetStateAction<CreateSubscriptionFormData>>;
  durationType: string;
  setDurationType: (id: string) => void;
  period: string;
  setPeriod: (id: string) => void;
  cycle: string;
  setCycle: (id: string) => void;
  cycleOptions: { id: string; label: string }[];
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  formatPriceWithSpaces: (price: string) => string;
  clampPriceToMax: (value: string) => string;
  maxPriceFormatted: string;
  getBillingDescription: (
    price: string,
    cycle: string,
    period: string,
    formatPriceWithSpacesFn: (p: string) => string
  ) => string;
  isSubmitting: boolean;
  isPreviewStep: boolean;
  modifiedBy: string;
  modifiedDate: string;
  selectedOffer: string;
  setSelectedOffer: React.Dispatch<React.SetStateAction<string>>;
};

// ============================================================================
// Mappers: API data → Form values
// ============================================================================

/**
 * Maps API durationType (MONTHLY/YEARLY) to form value (Miesięczny/Roczny)
 */
const mapDurationTypeFromApi = (
  durationType: SubscriptionPlan['durationType']
): string => {
  return durationType === 'YEARLY' ? 'Roczny' : 'Miesięczny';
};

/**
 * Maps API billingCycle (MONTHLY/QUARTERLY/YEARLY) to form value (Miesięczny/kwartalny/Roczny)
 */
const mapBillingCycleFromApi = (
  billingCycle: SubscriptionPlan['billingCycle']
): string => {
  const label = BILLING_CYCLE_LABELS[billingCycle];
  return label || 'Miesięczny';
};

/**
 * Maps API duration + durationType to form period value
 */
const mapPeriodFromApi = (
  duration: number,
  durationType: SubscriptionPlan['durationType']
): string => {
  return formatDuration(duration, durationType);
};

/**
 * Maps API price to form price string (with spaces for formatting)
 */
const mapPriceFromApi = (price: number): string => {
  return String(price);
};

/**
 * Maps SubscriptionPlan to form data
 */
const mapPlanToFormData = (
  plan: SubscriptionPlan
): CreateSubscriptionFormData => {
  return {
    planName: plan.planName || '',
    description: plan.planDescription || '',
    price: mapPriceFromApi(plan.price),
    userCount: plan.limits?.numberOfUsers?.toString() || '',
    facilityCount: plan.limits?.numberOfFacilities?.toString() || '',
    qmsDocCount: plan.limits?.numberOfQmsDocuments?.toString() || '',
  };
};

/**
 * Extracts user name from createdBy/updatedBy field
 */
const extractUserName = (
  value:
    | string
    | { name?: string; email?: string; [key: string]: unknown }
    | null
    | undefined
): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value.trim() ? value : '—';
  }
  if (typeof value === 'object') {
    if (value.name && typeof value.name === 'string') {
      return value.name.trim() ? value.name : '—';
    }
    if (value.email && typeof value.email === 'string') {
      return value.email.trim() ? value.email : '—';
    }
  }
  return '—';
};

/**
 * Formats date to Polish format
 */
const formatDate = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return `${year}/${month}/${day}, ${hour}:${minute}:${second}`;
};

// ============================================================================
// Hook
// ============================================================================

export function useEditSubscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams<{ planId: string }>();

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);

  // Track if data has been loaded to prevent reloading on navigation (use ref to avoid re-renders)
  const hasLoadedDataRef = useRef(false);

  // Sync isPreviewStep with URL path
  const isPreviewInUrl = useMemo(
    () => location.pathname.includes('/Podgląd-planu'),
    [location.pathname]
  );

  // Form state
  const [durationType, setDurationType] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [cycle, setCycle] = useState<string>('');
  const [formData, setFormData] = useState<CreateSubscriptionFormData>({
    planName: '',
    description: '',
    price: '',
    userCount: '',
    facilityCount: '',
    qmsDocCount: '',
  });
  const [originalFormData, setOriginalFormData] =
    useState<CreateSubscriptionFormData>({
      planName: '',
      description: '',
      price: '',
      userCount: '',
      facilityCount: '',
      qmsDocCount: '',
    });
  const [originalDurationType, setOriginalDurationType] = useState<string>('');
  const [originalPeriod, setOriginalPeriod] = useState<string>('');
  const [originalCycle, setOriginalCycle] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Read-only fields
  const [modifiedBy, setModifiedBy] = useState<string>('—');
  const [modifiedDate, setModifiedDate] = useState<string>('—');

  // Status state
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  // UI state - sync with URL
  const [isPreviewStep, setIsPreviewStep] = useState(isPreviewInUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<string>('');
  const [originalSelectedOffer, setOriginalSelectedOffer] =
    useState<string>('');

  // Status dialog state
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState<boolean>(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState<boolean>(false);

  // Status update snackbar state
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState<boolean>(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState<string>('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] =
    useState<boolean>(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState<string>('');

  // Derived
  const cycleOptions = getCycleOptions(durationType, period);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (
      formData.planName !== originalFormData.planName ||
      formData.description !== originalFormData.description ||
      formData.price !== originalFormData.price ||
      formData.userCount !== originalFormData.userCount ||
      formData.facilityCount !== originalFormData.facilityCount ||
      formData.qmsDocCount !== originalFormData.qmsDocCount ||
      durationType !== originalDurationType ||
      period !== originalPeriod ||
      cycle !== originalCycle ||
      selectedOffer !== originalSelectedOffer
    ) {
      return true;
    }
    return false;
  }, [
    formData,
    originalFormData,
    durationType,
    originalDurationType,
    period,
    originalPeriod,
    cycle,
    originalCycle,
    selectedOffer,
    originalSelectedOffer,
  ]);

  // Sync isPreviewStep with URL changes
  useEffect(() => {
    setIsPreviewStep(isPreviewInUrl);
  }, [isPreviewInUrl]);

  // Reset hasLoadedDataRef when planId changes
  useEffect(() => {
    hasLoadedDataRef.current = false;
  }, [planId]);

  // Load plan data - only once per planId
  useEffect(() => {
    const loadPlanData = async () => {
      if (!planId) {
        setIsLoading(false);
        return;
      }

      // Don't reload if data is already loaded (prevents resetting form on navigation)
      // Also check if form already has data (planName is a good indicator)
      if (hasLoadedDataRef.current || formData.planName.trim() !== '') {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await viewSubscriptionPlan({ planId });
        if (response.forbidden) {
          setAccessError(true);
          setIsLoading(false);
          return;
        }
        if (response.success && response.data) {
          const plan = response.data;

          // Map API data to form values
          const mappedFormData = mapPlanToFormData(plan);
          const mappedDurationType = mapDurationTypeFromApi(plan.durationType);
          const mappedPeriod = mapPeriodFromApi(
            plan.duration,
            plan.durationType
          );
          const mappedCycle = mapBillingCycleFromApi(plan.billingCycle);

          // Set form data
          setFormData(mappedFormData);
          setOriginalFormData(mappedFormData);
          setDurationType(mappedDurationType);
          setOriginalDurationType(mappedDurationType);
          setPeriod(mappedPeriod);
          setOriginalPeriod(mappedPeriod);
          setCycle(mappedCycle);
          setOriginalCycle(mappedCycle);

          // Set read-only fields
          setModifiedBy(extractUserName(plan.updatedBy));
          setModifiedDate(formatDate(plan.updatedAt));

          // Set status
          setStatus(plan.status || 'active');

          // Set selected offer
          const mappedOfferId = plan.offer?.offerId || plan.offerId || '';
          setSelectedOffer(mappedOfferId);
          setOriginalSelectedOffer(mappedOfferId);

          // Mark data as loaded (using ref to avoid re-renders)
          hasLoadedDataRef.current = true;
        } else {
          // Error loading plan - navigate to list
          navigate('/Plany', { replace: true });
        }
      } catch (error) {
        console.error('Error loading plan data:', error);
        navigate('/Plany', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    loadPlanData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // Reset period and cycle when period type changes
  useEffect(() => {
    if (durationType !== originalDurationType) {
      setPeriod('');
      setCycle('');
    }
  }, [durationType, originalDurationType]);

  // Reset cycle when period changes
  useEffect(() => {
    if (period !== originalPeriod) {
      setCycle('');
    }
  }, [period, originalPeriod]);

  // Auto-fill cycle when there is only one option
  useEffect(() => {
    const options = getCycleOptions(durationType, period);
    const singleOption = options[0];
    if (options.length === 1 && singleOption && !cycle) {
      setErrors((prev) => ({ ...prev, cycle: '' }));
      setCycle(singleOption.id);
    }
  }, [durationType, period, cycle]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!planId) {
        setSnackbarError(ERROR_MESSAGES.DEFAULT_ERROR);
        setIsSnackbarOpen(true);
        return;
      }

      const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
      const newErrors: FormErrors = {};

      if (!formData.planName.trim()) {
        newErrors.planName = requiredMessage;
      }
      if (!formData.description.trim()) {
        newErrors.description = requiredMessage;
      }
      if (!durationType) {
        newErrors.durationType = requiredMessage;
      }
      if (!period) {
        newErrors.period = requiredMessage;
      }
      if (!cycle) {
        newErrors.cycle = requiredMessage;
      }
      const priceNum = parseFloat(formData.price.replace(/\s/g, ''));
      if (
        formData.price.trim() === '' ||
        Number.isNaN(priceNum) ||
        priceNum < 0
      ) {
        newErrors.price = requiredMessage;
      } else if (priceNum > Number.MAX_SAFE_INTEGER) {
        newErrors.price = SUBSCRIPTION_ERROR_MESSAGES.PRICE_TOO_BIG;
      }
      if (!formData.userCount.trim()) {
        newErrors.userCount = requiredMessage;
      }
      if (!formData.facilityCount.trim()) {
        newErrors.facilityCount = requiredMessage;
      }

      setErrors(newErrors);
      const hasFieldErrors = Object.keys(newErrors).length > 0;
      if (hasFieldErrors) {
        return;
      }

      // First click: validation passed → go to "preview" step (button becomes "Zapisz")
      if (!isPreviewStep) {
        setIsPreviewStep(true);
        // Update URL to show "Podgląd planu" in breadcrumb
        if (planId) {
          navigate(`/Plany/Edycja-planu/${planId}/Podgląd-planu`, {
            replace: true,
          });
        }
        return;
      }

      // Second click: call API
      setIsSubmitting(true);
      setErrors({});

      try {
        const result = await editSubscriptionPlan({
          planId,
          planName: formData.planName.trim(),
          planDescription: formData.description.trim(),
          durationType,
          period,
          cycle,
          price: formData.price,
          ...(formData.userCount && { userCount: formData.userCount }),
          ...(formData.facilityCount && {
            facilityCount: formData.facilityCount,
          }),
          ...(formData.qmsDocCount && { qmsDocCount: formData.qmsDocCount }),
          ...(selectedOffer && { offerId: selectedOffer }),
        });

        if (result.success) {
          navigate('/Plany', {
            replace: true,
            state: { showEditPlanSuccess: true },
          });
          return;
        }

        if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...result.fieldErrors }));
        }
        const isPlanNameExistsError =
          result.error === ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS ||
          result.error
            ?.toLowerCase()
            .includes('subscription plan name already exists');
        if (isPlanNameExistsError) {
          setErrors((prev) => ({
            ...prev,
            planName: SUBSCRIPTION_ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS,
          }));
        } else if (result.error) {
          setSnackbarError(result.error);
          setIsSnackbarOpen(true);
        }
      } catch {
        setSnackbarError(ERROR_MESSAGES.NETWORK_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      durationType,
      period,
      cycle,
      navigate,
      planId,
      isPreviewStep,
      selectedOffer,
    ]
  );

  const handleCancel = useCallback(() => {
    if (isPreviewStep) {
      setIsPreviewStep(false);
      return;
    }
    if (!hasChanges) {
      navigate('/Plany', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [isPreviewStep, hasChanges, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    // Navigate back to edit path if we're in preview mode
    if (isPreviewStep && planId) {
      navigate(`/Plany/Edycja-planu/${planId}`, { replace: true });
    }
    navigate('/Plany', { replace: true });
  }, [navigate, isPreviewStep, planId]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  // Status change handler - opens dialog instead of directly changing status
  const handleStatusChange = useCallback(() => {
    setIsStatusDialogOpen(true);
  }, []);

  // Status dialog handlers
  const handleCloseStatusDialog = useCallback(() => {
    if (!isStatusSubmitting) {
      setIsStatusDialogOpen(false);
    }
  }, [isStatusSubmitting]);

  // Handle status update confirmation
  const handleConfirmStatusUpdate = useCallback(async () => {
    if (!planId) {
      setStatusErrorMessage(ERROR_MESSAGES.DEFAULT_ERROR);
      setShowStatusErrorSnackbar(true);
      return;
    }

    setIsStatusSubmitting(true);
    const isActive = status === 'inactive'; // Will activate if currently inactive

    try {
      const result =
        await updateSubscriptionStatusApiService.updateSubscriptionStatus({
          planId,
          isActive,
        });

      if (result.success) {
        // Update status state
        const newStatus: 'active' | 'inactive' = isActive
          ? 'active'
          : 'inactive';
        setStatus(newStatus);

        // Show success snackbar
        const message = isActive
          ? 'Plan subskrypcji został pomyślnie aktywowany.'
          : 'Plan subskrypcji został pomyślnie dezaktywowany.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsStatusDialogOpen(false);
      } else {
        // Show error snackbar
        setStatusErrorMessage(
          result.error || 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
      }
    } catch (_error) {
      // Show error snackbar
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [planId, status]);

  // Status snackbar handlers
  const handleCloseStatusSuccessSnackbar = useCallback(() => {
    setShowStatusSuccessSnackbar(false);
    setStatusSuccessMessage('');
  }, []);

  const handleCloseStatusErrorSnackbar = useCallback(() => {
    setShowStatusErrorSnackbar(false);
    setStatusErrorMessage('');
  }, []);

  return {
    // Loading state
    isLoading,
    accessError,

    // Form state
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,

    // Read-only fields
    modifiedBy,
    modifiedDate,

    // UI state
    isPreviewStep,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    hasChanges,
    selectedOffer,
    setSelectedOffer,

    // Handlers
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,

    // Helpers (for form JSX)
    formatPriceWithSpaces,
    clampPriceToMax,
    getBillingDescription,

    // Status management
    status,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
  };
}
